import React, { useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    CheckOutlined,
    DeleteOutlined,
    EditOutlined,
    LockOutlined,
    PlusOutlined,
    SaveOutlined,
    StopOutlined,
} from '@ant-design/icons';
import HasPermission from '@components/common/elements/HasPermission';
import { BaseTooltip } from '@components/common/form/BaseTooltip';
import SearchForm from '@components/common/form/SearchForm';
import { commonStatus, commonStatusColor, DEFAULT_TABLE_ITEM_SIZE, DEFAULT_TABLE_PAGE_START } from '@constants';
import { errorCodes } from '@constants/ErrorCode';
import { commonMessage } from '@locales/intl';
import { showErrorMessage, showSucsessMessage } from '@services/notifyService';
import { validatePermission } from '@utils';
import { Button, Divider, Flex, Form, Modal, Tag } from 'antd';
import qs from 'query-string';

import useAuth from './useAuth';
import useFetch from './useFetch';
import useQueryParams from './useQueryParams';
import useTranslate from './useTranslate';

const tableColumnMessage = defineMessages({
    action: 'Action',
    status: 'Status',
});

const message = defineMessages({
    error: '{title} {objectName} failed',
    success: '{title} {objectName} successfully',
    objectName: 'Food Groups',
    titleModal: '{title} {objectName}',
});

const notificationMessage = defineMessages({
    deleteSuccess: 'Delete {objectName} successfully',
    deleteTitle: 'Are you sure want to delete this {objectName}?',
    ok: 'Ok',
    cancel: 'Cancel',
});

const defineStatusMessage = defineMessages({
    active: 'Active',
    pending: 'Pending',
    inactive: 'Inactive',
    delete: 'Deleted',
    lock: 'Lock',
});

const statusMessage = {
    [commonStatus.ACTIVE]: defineStatusMessage.active,
    [commonStatus.PENDING]: defineStatusMessage.pending,
    [commonStatus.INACTIVE]: defineStatusMessage.inactive,
    [commonStatus.DELETE]: defineStatusMessage.delete,
    [commonStatus.LOCK]: defineStatusMessage.lock,
};
const useListModal = ({
    apiConfig = {
        getList: null,
        delete: null,
        create: null,
        update: null,
        getById: null,
    },
    options = {
        objectName: '',
        pageSize: DEFAULT_TABLE_ITEM_SIZE,
        excludePermissions: {
            create: false,
            edit: false,
            delete: false,
        },
        ignoreQueryParams: [],
    },
    tabOptions = {
        queryPage: {},
        isTab: false,
    },
    override,
} = {}) => {
    const { params: queryParams, setQueryParams, serializeParams, deserializeParams } = useQueryParams();
    const translate = useTranslate();
    const [data, setData] = useState(0);
    const [detail, setDetail] = useState(0);
    const [currentPageTab, setCurrentPageTab] = useState(0);
    const [isChanged, setChange] = useState(false);
    const isEditing = useMemo(() => !!detail, [detail]);
    const titleModal = useMemo(() => {
        const title = translate.formatMessage(message.titleModal, {
            title: detail
                ? translate.formatMessage(commonMessage.update)
                : translate.formatMessage(commonMessage.create),
            objectName: options.objectName,
        });
        return title;
    }, [detail]);

    const { execute: executeSave, loading: loadingSave } = useFetch(detail ? apiConfig.update : apiConfig.create);
    const { execute: executeGet } = useFetch(apiConfig.getById);

    const [loading, setLoading] = useState(false);
    const { execute: executeGetList } = useFetch(apiConfig.getList);
    const { execute: executeDelete } = useFetch(apiConfig.delete);
    const location = useLocation();
    const { listData } = location.state ?? {};
    const [openModal, setOpenModal] = useState(false);
    const [form] = Form.useForm();
    const [pagination, setPagination] = useState(
        listData
            ? listData.pagination
            : {
                pageSize: options.pageSize,
                total: 0,
                current: 1,
            },
    );
    const { pathname: pagePath } = useLocation();
    const { permissions } = useAuth();
    const navigate = useNavigate();
    const intl = useIntl();

    const queryFilter = useMemo(() => deserializeParams(queryParams), [queryParams]);

    const onCancelModal = () => {
        setOpenModal(false);
        setChange(false);
        setDetail(null);
        form.resetFields();
    };

    const getDataDetailMapping = (data) => {
        return data;
    };

    const onShowModal = (id) => {
        if (id) {
            setLoading(true);
            executeGet({
                pathParams: { id },
                onCompleted: ({ data }) => {
                    setLoading(false);
                    setDetail(mixinFuncs.getDataDetailMapping(data));
                    setOpenModal(true);
                },
                onError: (err) => {
                    setLoading(false);
                },
            });
        } else setOpenModal(true);
    };

    const hasPermission = (requiredPermissions) => {
        return validatePermission(requiredPermissions, permissions);
    };

    const mappingData = (response) => {
        return {
            data: response.data.content,
            total: response.data.totalElements,
        };
    };

    const handleGetListError = (error) => {
        showErrorMessage(translate.formatMessage(commonMessage.error), translate);
    };

    const onCompletedGetList = (response) => {
        const { data, total } = mixinFuncs.mappingData(response);

        setData(data);
        setPagination((p) => ({ ...p, total }));
    };

    const prepareGetListPathParams = () => {
        return {};
    };

    const handleFetchList = (params, isReload) => {
        if (!apiConfig.getList) throw new Error('apiConfig.getList is not defined');
        if (listData && !isReload) {
            setData(listData.data);
            setPagination(listData.pagination);
        } else {
            setLoading(true);
            executeGetList({
                pathParams: mixinFuncs.prepareGetListPathParams(),
                params,
                onCompleted: (response) => {
                    mixinFuncs.onCompletedGetList(response);
                    setLoading(false);
                },
                onError: (error) => {
                    mixinFuncs.handleGetListError(error);
                    setLoading(false);
                },
            });
        }
    };

    const prepareGetListParams = (filter) => {
        const copyFilter = { ...filter };

        const page = parseInt(queryParams.get('page'));
        copyFilter.page = page > 0 ? page - 1 : DEFAULT_TABLE_PAGE_START;

        copyFilter.size = options.pageSize;

        return copyFilter;
    };

    const getList = (isReload = false) => {
        let querys = queryFilter;
        if (options.ignoreQueryParams?.length > 0) {
            Object.entries(queryFilter).forEach(([key, value]) => {
                if (options.ignoreQueryParams.includes(key)) delete querys[key];
                else querys[key] = value;
            });
        }

        const params = mixinFuncs.prepareGetListParams(querys);

        mixinFuncs.handleFetchList({ ...params }, isReload);
    };

    const changeFilter = (filter) => {
        setQueryParams(serializeParams(filter));
    };

    function changePagination(page) {
        if (tabOptions.isTab) {
            setCurrentPageTab(page.current);
        } else {
            queryParams.set('page', page.current);
            setQueryParams(queryParams);
        }
    }

    const handleDeleteItemError = (error) => {
        const errorCode = error?.response?.data?.code;
        const errorMessage = errorCodes[errorCode]?.message;
        if (errorMessage) {
            showErrorMessage(translate.formatMessage(errorMessage), translate);
        } else {
            showErrorMessage(
                error.message ||
                intl.formatMessage(errorMessage.deleteItemFailed, { objectName: options.objectName }),
                translate,
            );
        }
    };

    const onDeleteItemCompleted = (id) => {
        const currentPage = queryParams.get('page');
        if (data.length === 1 && currentPage > 1) {
            queryParams.set('page', currentPage - 1);
            setQueryParams(queryParams);
        } else {
            mixinFuncs.getList(true);
            // setData((data) => data.filter((item) => item.id !== id));
        }
    };

    const handleDeleteItem = (id) => {
        setLoading(true);
        executeDelete({
            pathParams: { id },
            onCompleted: (response) => {
                mixinFuncs.onDeleteItemCompleted(id);

                showSucsessMessage(
                    intl.formatMessage(notificationMessage.deleteSuccess, {
                        objectName: options.objectName,
                    }),
                    translate,
                );
            },
            onError: (error) => {
                mixinFuncs.handleDeleteItemError(error);
                setLoading(false);
            },
        });
    };

    const showDeleteItemConfirm = (id) => {
        if (!apiConfig.delete) throw new Error('apiConfig.delete is not defined');

        Modal.confirm({
            title: intl.formatMessage(notificationMessage.deleteTitle, { objectName: options.objectName }),
            content: '',
            okText: intl.formatMessage(notificationMessage.ok),
            cancelText: intl.formatMessage(notificationMessage.cancel),
            centered: true,
            onOk: () => {
                mixinFuncs.handleDeleteItem(id);
            },
        });
    };

    const handleChangeStatusError = (error) => {
        showErrorMessage(error.message, translate);
    };

    // This function is currently not needed
    const { execute: executeChangeStatus } = useFetch(apiConfig.update);
    const handleChangeStatus = (id, status) => {
        const newStatus = status === commonStatus.ACTIVE ? -1 : 1;
        executeChangeStatus({
            pathParams: { id },
            data: { id, status: newStatus },
            onCompleted: (response) => {
                mixinFuncs.getList(true);
                showSucsessMessage(
                    newStatus === 1 ? translate.formatMessage(commonMessage.updateStatusSuccess) : translate.formatMessage(commonMessage.lockStatusSuccess),
                    translate,
                );
            },
            onError: mixinFuncs.handleChangeStatusError,
        });
    };

    const showChangeStatusConfirm = (id, status) => {
        if (!apiConfig.changeStatus) throw new Error('apiConfig.changeStatus is not defined');

        Modal.confirm({
            title:
                status === commonStatus.ACTIVE
                    ? translate.formatMessage(commonMessage.lockConfirm, { objectName: options.objectName })
                    : translate.formatMessage(commonMessage.activeConfirm, { objectName: options.objectName }),
            content: '',
            okText: translate.formatMessage(commonMessage.yes),
            cancelText: translate.formatMessage(commonMessage.no),
            centered: true,
            onOk: () => {
                mixinFuncs.handleChangeStatus(id, status);
            },
        });
    };

    const additionalActionColumnButtons = () => {
        return {};
    };

    const actionColumnButtons = (additionalButtons = {}) => ({
        delete: ({ id, buttonProps }) => {
            if (!options?.excludePermissions?.delete && !mixinFuncs.hasPermission(apiConfig.delete?.baseURL))
                return null;

            return (
                <BaseTooltip type="delete" objectName={options.objectName}>
                    <Button
                        {...buttonProps}
                        type="link"
                        onClick={(e) => {
                            e.stopPropagation();
                            mixinFuncs.showDeleteItemConfirm(id);
                        }}
                        style={{ padding: 0 }}
                    >
                        <DeleteOutlined style={{ color: 'red' }} />
                    </Button>
                </BaseTooltip>
            );
        },
        changeStatus: ({ id, status, buttonProps }) => {
            return (
                <Button
                    {...buttonProps}
                    type="link"
                    onClick={(e) => {
                        e.stopPropagation();
                        mixinFuncs.showChangeStatusConfirm(id, status);
                    }}
                    style={{ padding: 0 }}
                >
                    {status === commonStatus.ACTIVE ? <LockOutlined /> : <CheckOutlined />}
                </Button>
            );
        },
        edit: ({ buttonProps, ...dataRow }) => {
            if (
                !options?.excludePermissions?.edit &&
                !mixinFuncs.hasPermission([apiConfig.update?.baseURL, apiConfig.getById?.baseURL])
            )
                return null;

            return (
                <BaseTooltip type="edit" objectName={options.objectName}>
                    <Button
                        {...buttonProps}
                        onClick={(e) => {
                            e.stopPropagation();
                            onShowModal(dataRow.id);
                        }}
                        type="link"
                        style={{ padding: 0 }}
                    >
                        <EditOutlined />
                    </Button>
                </BaseTooltip>
            );
        },
        ...additionalButtons,
    });

    const getListData = useMemo(() => {
        return {
            data,
            pagination,
        };
    }, [data, pagination]);

    const createActionColumnButtons = (actions, data) => {
        const actionButtons = [];
        const buttons = actionColumnButtons(mixinFuncs.additionalActionColumnButtons());

        Object.entries(actions).forEach(([key, value]) => {
            let _value = value;
            if (typeof value === 'function') {
                _value = value(data);
            }
            if (_value && buttons[key]) {
                actionButtons.push(buttons[key]);
            }
        });

        return actionButtons;
    };

    const checkPermission = (actions) => {
        let isShow = false;
        Object.entries(actions).forEach(([type, value]) => {
            if (value || value?.show) {
                switch (type) {
                                case 'delete':
                                    if (mixinFuncs.hasPermission(apiConfig.delete?.baseURL)) isShow = true;
                                    break;
                                case 'edit':
                                    if (mixinFuncs.hasPermission([apiConfig.update?.baseURL, apiConfig.getById?.baseURL]))
                                        isShow = true;
                                    break;
                                default:
                                    if (mixinFuncs.hasPermission(value?.permissions) || value.ignorePermission) isShow = true;
                                    break;
                }
                return;
            }
        });
        return isShow;
    };

    const renderActionColumn = (
        action = { edit: false, delete: false, changeStatus: false },
        columnsProps,
        buttonProps,
        fixed = 'right',
    ) => {
        const isRender = checkPermission(action);
        if (!isRender) return;
        return {
            align: 'center',
            fixed,
            ...columnsProps,
            title: intl.formatMessage(tableColumnMessage.action),
            render: (data) => {
                const buttons = [];
                const actionButtons = mixinFuncs.createActionColumnButtons(action, data);
                actionButtons.forEach((ActionItem, index) => {
                    if (ActionItem({ ...data, ...buttonProps })) {
                        buttons.push(ActionItem);
                    }
                });

                return (
                    <span style={{ whiteSpace: 'nowrap' }}>
                        {buttons.map((ActionItem, index) => (
                            <React.Fragment key={index}>
                                {index > 0 && <Divider type="vertical" />}
                                <span>
                                    {ActionItem({ ...data, ...buttonProps }) ? (
                                        <ActionItem {...data} {...buttonProps} />
                                    ) : null}
                                </span>
                            </React.Fragment>
                        ))}
                    </span>
                );
            },
        };
    };

    const renderIdColumn = (columnsProps) => ({
        title: translate.formatMessage(commonMessage.id),
        dataIndex: 'id',
        width: '50px',
        align: 'left',
        ...columnsProps,
    });

    const renderStatusColumn = (columnsProps) => {
        return {
            title: intl.formatMessage(tableColumnMessage.status),
            dataIndex: 'status',
            align: 'center',
            ...columnsProps,
            render: (status) => {
                if (status === undefined || status === null || !statusMessage[status]) {
                    return null;
                }

                return (
                    <Tag color={commonStatusColor[status] || 'default'}>
                        <div style={{ padding: '0 4px', fontSize: 14 }}>
                            {intl.formatMessage(statusMessage[status])}
                        </div>
                    </Tag>
                );
            },
        };
    };

    const getItemDetailLink = (dataRow) => {
        return `${pagePath}/${dataRow.id}`;
    };

    const getCreateLink = () => {
        return `${pagePath}/create`;
    };

    const generateParams = (params = {}, pageable = true, currentParams = false) => {
        let parseParams = params;
        if (pagination.current > 0) parseParams.page = pagination.current;

        return `?${qs.stringify(parseParams)}`;
    };

    const renderActionBar = ({ objectName, type, style, onBulkDelete, selectedRows = [], buttons } = {}) => {
        return (
            <Flex justify="end">
                <HasPermission requiredPermissions={apiConfig.create?.baseURL}>
                    <Button type="primary" onClick={() => onShowModal()}>
                        <PlusOutlined />
                    </Button>
                </HasPermission>
            </Flex>
        );
    };

    const handleFilterSearchChange = (values) => {
        mixinFuncs.changeFilter(values);
    };

    const renderSearchForm = ({
        fields = [],
        getFormInstance,
        hiddenAction,
        className,
        initialValues,
        onSearch,
        onReset,
        alignSearchField = 'left',
        searchParams,
    }) => {
        return (
            <SearchForm
                getFormInstance={getFormInstance}
                alignSearchField={alignSearchField}
                fields={fields}
                initialValues={initialValues}
                searchParams={searchParams}
                onSearch={(values) => {
                    onSearch?.(values);
                    mixinFuncs.handleFilterSearchChange(values);
                }}
                hiddenAction={hiddenAction}
                className={className}
                onReset={() => {
                    mixinFuncs.changeFilter({});
                    onReset?.();
                }}
            />
        );
    };

    const filterLanguage = (dataRow = []) => {
        let renderItem;
        dataRow.filter((item) => {
            if (item.languageId === '1') renderItem = item;
        });
        return renderItem || {};
    };

    const setIsChangedFormValues = (flag) => {
        if (flag !== isChanged) {
            setChange(flag);
        }
    };

    const onSaveCompleted = (responseData) => {
        mixinFuncs.getList();
        onCancelModal();
        showSucsessMessage(
            translate.formatMessage(message.success, {
                title: detail
                    ? translate.formatMessage(commonMessage.update)
                    : translate.formatMessage(commonMessage.create),
                objectName: options.objectName,
            }),
            translate,
        );
    };

    const onSaveError = (err, handleError) => {
        const errorCode = err?.code;
        const errorMessage = errorCodes[errorCode]?.message;
        onCancelModal();
        if (errorMessage) {
            showErrorMessage(translate.formatMessage(errorMessage), translate);
        } else {
            showErrorMessage(
                translate.formatMessage(message.error, {
                    title: detail
                        ? translate.formatMessage(commonMessage.update)
                        : translate.formatMessage(commonMessage.create),
                    objectName: options.objectName,
                }),
                translate,
            );
        }
    };

    const onSave = (values, callback) => {
        if (detail?.id) values.id = detail?.id;
        executeSave({
            data: {
                ...values,
            },
            onCompleted: (res) => {
                mixinFuncs.onSaveCompleted();
            },
            onError: (err) => {
                mixinFuncs.onSaveError(err, callback);
            },
        });
    };

    const renderActions = (customDisabledSubmitValue) => {
        const disabledSubmit = customDisabledSubmitValue !== undefined ? customDisabledSubmitValue : !isChanged;
        return (
            <Flex justify="end" style={{ width: '99%', marginTop: '-10px' }} gap={8}>
                <Button
                    danger
                    key="cancel"
                    onClick={(e) => {
                        e.stopPropagation();
                        onCancelModal();
                    }}
                    icon={<StopOutlined />}
                >
                    {intl.formatMessage(commonMessage.cancel)}
                </Button>
                <Button
                    key="submit"
                    htmlType="submit"
                    type="primary"
                    loading={loadingSave}
                    disabled={disabledSubmit}
                    icon={<SaveOutlined />}
                >
                    {detail ? intl.formatMessage(commonMessage.update) : intl.formatMessage(commonMessage.create)}
                </Button>
            </Flex>
        );
    };

    const overrideHandler = () => {
        const centralizedHandler = {
            hasPermission,
            mappingData,
            handleGetListError,
            handleFetchList,
            prepareGetListParams,
            getList,
            changeFilter,
            showDeleteItemConfirm,
            handleDeleteItem,
            handleDeleteItemError,
            createActionColumnButtons,
            showChangeStatusConfirm,
            handleChangeStatus,
            handleChangeStatusError,
            renderActionColumn,
            renderIdColumn,
            getItemDetailLink,
            getCreateLink,
            renderStatusColumn,
            changePagination,
            additionalActionColumnButtons,
            renderActionBar,
            onCompletedGetList,
            onDeleteItemCompleted,
            filterLanguage,
            renderSearchForm,
            handleFilterSearchChange,
            prepareGetListPathParams,
            generateParams,
            setQueryParams,
            onSaveCompleted,
            onSaveError,
            renderActions,
            getDataDetailMapping,
        };

        override?.(centralizedHandler);

        return centralizedHandler;
    };

    const mixinFuncs = overrideHandler();

    useEffect(() => {
        mixinFuncs.getList();

        const page = parseInt(queryFilter.page);
        if (page > 0 && page !== pagination.current) {
            setPagination((p) => ({ ...p, current: page }));
        } else if (page < 1 || isNaN(page)) {
            setPagination((p) => ({ ...p, current: 1 }));
        }
    }, [queryParams, pagePath]);

    return {
        loading,
        loadingSave,
        data,
        setData,
        queryFilter,
        actionColumnButtons,
        changeFilter,
        changePagination,
        pagination,
        mixinFuncs,
        getList,
        setLoading,
        pagePath,
        serializeParams,
        queryParams,
        setQueryParams,
        titleModal,
        detail,
        setDetail,
        executeSave,
        onShowModal,
        onCancelModal,
        openModal,
        setIsChangedFormValues,
        form,
        onSave,
        isEditing,
        isChanged,
    };
};

export default useListModal;
